import React, { useState } from 'react';
import round from '../../../helpers/round';
import { Alert, Typography, Box, IconButton, Collapse, Tooltip, useMediaQuery } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from '@mui/material/Link';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const PilerockBcResult = ({ result }) => {
  const {
    socketDiameter,
    socketLength,
    c0,
    rqd,
    pileLength,
    gammaConcrete,
    sigmaConcrete,
    nms,
    qsrOption,
    FsSt,
    FsSs,
    FSt,
    FSs,
    FTension,
  } = result.inputs;

  const {
    canUserSeeCalculationSteps,
    ae,
    isAeCapped,
    cm,
    sigmac,
    sigmacPsi,
    qsrPsiMin,
    qsrPsiMax,
    qsrPsiAve,
    qsrPsi,
    qsr,
    QSR,
    pileTipArea,
    QTR,
    pileWeight,
    qUltCompression,
    qAllCompressionStatic,
    qAllCompressionSeismic,
    qUltTension,
    qAllTension,
    qAllCompressionStaticIsEnough,
    qAllCompressionSeismicIsEnough,
    qAllTensionIsEnough,
  } = result.outputs;

  const isSmallScreen = useMediaQuery('(max-width:700px)');

  const PileRockBearingSummary = () => {
    return (
      <Box sx={{ my: 1, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} gutterBottom>
            Axial Capacity (AASHTO 2002 – Article 4.6.5.1)
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            The ultimate axial capacity of a drilled shaft socketed into rock consists of side resistance along the socket and end
            bearing at the tip. According to AASHTO (2002), the ultimate load for compression and uplift shall be determined as:
          </Typography>

          <Typography color="text.secondary" sx={{ marginTop: '10px', marginBottom: '10px', lineHeight: 1.6, fontWeight: 600 }}>
            <math display="block">
              <msub>
                <mi>Q</mi>
                <mi>ult</mi>
              </msub>
              <mo>=</mo>
              <msub>
                <mi>Q</mi>
                <mi>SR</mi>
              </msub>
              <mo>+</mo>
              <msub>
                <mi>Q</mi>
                <mi>TR</mi>
              </msub>
              <mo>−</mo>
              <mi>W</mi>
              <mtext> (Compression)</mtext>
            </math>

            <br />

            <math display="block">
              <msub>
                <mi>Q</mi>
                <mi>ult</mi>
              </msub>
              <mo>=</mo>
              <mn>0.7</mn>
              <msub>
                <mi>Q</mi>
                <mi>SR</mi>
              </msub>
              <mo>+</mo>
              <mi>W</mi>
              <mtext> (Uplift)</mtext>
            </math>
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.6 }}>
            The allowable (working) axial load is obtained by dividing the ultimate capacity by the factor of safety:
          </Typography>

          <Typography color="text.secondary" sx={{ marginTop: '10px', marginBottom: '5px', lineHeight: 1.6, fontWeight: 600 }}>
            <math display="block">
              <msub>
                <mi>Q</mi>
                <mi>all</mi>
              </msub>
              <mo>=</mo>
              <mfrac>
                <msub>
                  <mi>Q</mi>
                  <mi>ult</mi>
                </msub>
                <mi>FS</mi>
              </mfrac>
            </math>
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.6 }}>
            where&nbsp;
            <i>
              Q<sub>ult</sub>
            </i>{' '}
            = ultimate axial load capacity,&nbsp;
            <i>
              Q<sub>all</sub>
            </i>{' '}
            = allowable axial load capacity,&nbsp;
            <i>
              Q<sub>SR</sub>
            </i>{' '}
            = ultimate side resistance of rock socket,&nbsp;
            <i>
              Q<sub>TR</sub>
            </i>{' '}
            = ultimate tip resistance of rock socket,&nbsp;
            <i>W</i> = weight of shaft,&nbsp;and&nbsp;
            <i>FS</i> = factor of safety.
          </Typography>
        </Alert>
      </Box>
    );
  };

  const SideResistance = () => {
    const [showAeDetails, setShowAeDetails] = useState(false);
    const [showCmDetails, setShowCmDetails] = useState(false);
    const [showSigmaCDetails, setShowSigmaCDetails] = useState(false);
    const [showqsrDetails, setShowqsrDetails] = useState(false);
    const [showQsrDetails, setShowQsrDetails] = useState(false);

    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Side Resistance
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            The side resistance of a pile socketed into rock arises from shear transfer along the interface between the pile shaft
            and the surrounding rock. The main objective of this calculation is to determine the ultimate unit shear resistance (
            <i>
              q<sub>SR</sub>
            </i>
            ) at the shaft–rock interface, and consequently the ultimate side resistance (
            <i>
              Q<sub>SR</sub>
            </i>
            ) of the pile:
          </Typography>
          <Typography
            color="text.secondary"
            sx={{
              marginTop: '15px',
              marginBottom: '10px',
              lineHeight: 1.6,
              fontWeight: 600,
            }}
          >
            <math display="block">
              <msub>
                <mi>Q</mi>
                <mi>SR</mi>
              </msub>
              <mo>=</mo>
              <mi>π</mi>
              <mo>×</mo>
              <msub>
                <mi>B</mi>
                <mi>r</mi>
              </msub>
              <mo>×</mo>
              <msub>
                <mi>D</mi>
                <mi>r</mi>
              </msub>
              <mo>×</mo>
              <msub>
                <mi>q</mi>
                <mi>SR</mi>
              </msub>
            </math>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            Q<sub>SR</sub>: Ultimate side resistance of rock socket, B<sub>r</sub>: Diameter of rock socket, D<sub>r</sub>: Length
            of rock socket, q<sub>SR</sub>: Ultimate unit shear resistance along shaft/rock interface.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* ae reduction factor */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowAeDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showAeDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>
                  &alpha;<sub>E</sub>
                </b>{' '}
                (Reduction Factor): {ae ? ae.toFixed(2) : '—'}
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showAeDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    We can use the following formula to calculate the reduction factor &alpha;<sub>E</sub>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>α</mi>
                        <mi>E</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>0.0231</mn>
                      <mo>(</mo>
                      <mi>RQD</mi>
                      <mo>)</mo>
                      <mo>−</mo>
                      <mn>1.32</mn>
                      <mo>≥</mo>
                      <mn>0.15</mn>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    inserting the RQD value we have;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>α</mi>
                        <mi>E</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>0.0231</mn>
                      <mo>(</mo>
                      <mi>{rqd ? rqd.toFixed(2) : '—'}</mi>
                      <mo>)</mo>
                      <mo>−</mo>
                      <mn>1.32</mn>
                      <mo>≥</mo>
                      <mn>0.15</mn>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    And from here we will have;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>α</mi>
                        <mi>E</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>
                        {ae ? ae.toFixed(2) : '—'}
                        {isAeCapped && '*'}
                      </mn>
                    </math>
                  </Typography>
                  {isAeCapped && (
                    <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                      Note*: Since &alpha;<sub>E</sub> is calculated to be less than 0.15, its value is capped to 0.15.
                    </Typography>
                  )}
                </Collapse>
              )}
            </Alert>
            {/* Cm  */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowCmDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showCmDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>
                  C<sub>m</sub>
                </b>{' '}
                (Uniaxial compressive strength of rock mass): {cm ? cm.toFixed(2) : '—'} MPa
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showCmDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    We can use the following formula to calculate the uniaxial compressive strength of rock mass C<sub>m</sub>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>C</mi>
                        <mi>m</mi>
                      </msub>
                      <mo>=</mo>
                      <msub>
                        <mi>α</mi>
                        <mi>E</mi>
                      </msub>
                      <msub>
                        <mi>C</mi>
                        <mi>0</mi>
                      </msub>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    We have calculated the value of &alpha;<sub>E</sub> on the previous part, and C<sub>0</sub> is the uniaxial
                    compressive strength of intact rock which is provided in the inputs section. So we have;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>C</mi>
                        <mi>m</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>({ae ? ae.toFixed(2) : '—'})</mn>
                      <mn>({c0 ? c0.toFixed(2) : '—'})</mn>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    And from here we will have;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>C</mi>
                        <mi>m</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>{cm ? cm.toFixed(2) : '—'} MPa</mn>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* Sigma C  */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowSigmaCDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showSigmaCDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>
                  &sigma;<sub>c</sub>
                </b>{' '}
                (Uniaxial compressive strength of rock or concrete, whichever is weaker): {sigmac ? sigmac.toFixed(2) : '—'} MPa
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showSigmaCDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    At this step we need to update &sigma;<sub>c</sub> to the minimum of &sigma;<sub>c</sub> and C<sub>m</sub>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>σ</mi>
                        <mi>c</mi>
                      </msub>
                      <mo>=</mo>
                      <mi>Min</mi>
                      <mo>(</mo>
                      <msub>
                        <mi>σ</mi>
                        <mi>c</mi>
                      </msub>
                      <mo>,</mo>
                      <msub>
                        <mi>C</mi>
                        <mi>m</mi>
                      </msub>
                      <mo>)</mo>
                    </math>
                  </Typography>

                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    We know the value of &sigma;<sub>c</sub> from the inputs, and we have calculated the value of C<sub>m</sub> on
                    the previous part. So we have;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>σ</mi>
                        <mi>c</mi>
                      </msub>
                      <mo>=</mo>
                      <mi>Min</mi>
                      <mo>(</mo>
                      <mn>{sigmaConcrete ? sigmaConcrete.toFixed(2) : '—'} MPa</mn>
                      <mo>,</mo>
                      <mn>{cm ? cm.toFixed(2) : '—'} MPa</mn>
                      <mo>)</mo>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    And from here we will have;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>&sigma;</mi>
                        <mi>c</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>{sigmac ? sigmac.toFixed(2) : '—'} MPa</mn>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* qsr  */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowqsrDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showqsrDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>
                  q<sub>SR</sub>
                </b>{' '}
                (Ultimate unit shear resistance along shaft/rock interface): {qsr ? qsr.toFixed(0) : '—'} kPa
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showqsrDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    In order to calculate the ultimate unit shear resistance along shaft/rock interface, q<sub>SR</sub>, we need
                    to use AASHTO (2002) - Figure 4.6.5.3.1A. In this figure, we have &sigma;<sub>c</sub> in horizontal axis,
                    which we have already calculated. But we need to convert the units from MPa to psi to use the figure. So;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>σ</mi>
                        <mi>c</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>{sigmac ? sigmac.toFixed(2) : '—'} MPa</mn>
                      <mo>=</mo>
                      <mn>{sigmacPsi ? sigmacPsi.toFixed(2) : '—'} psi</mn>
                    </math>
                  </Typography>

                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    Using this value of &sigma;<sub>c</sub>, the q<sub>SR</sub> value can be selected as;
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center', // vertical alignment
                      justifyContent: 'center', // horizontal spacing control
                      gap: 2, // space between children (theme spacing units)
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      <math display="block">
                        <msub>
                          <mi>q</mi>
                          <mi>SR(min)</mi>
                        </msub>
                        <mo>=</mo>
                        <mn>{qsrPsiMin ? qsrPsiMin.toFixed(2) : '—'} psi</mn>
                      </math>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      <math display="block">
                        <msub>
                          <mi>q</mi>
                          <mi>SR(ave)</mi>
                        </msub>
                        <mo>=</mo>
                        <mn>{qsrPsiAve ? qsrPsiAve.toFixed(2) : '—'} psi</mn>
                      </math>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      <math display="block">
                        <msub>
                          <mi>q</mi>
                          <mi>SR(max)</mi>
                        </msub>
                        <mo>=</mo>
                        <mn>{qsrPsiMax ? qsrPsiMax.toFixed(2) : '—'} psi</mn>
                      </math>
                    </Typography>
                  </Box>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    Since you selected in the inputs section to use the{' '}
                    {qsrOption === 1 ? 'maximum' : qsrOption === 2 ? 'average' : 'minimum'} value, we have;
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mi>SR</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>{qsrPsi ? qsrPsi.toFixed(2) : '—'} psi</mn>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    Which is equivalent to;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mi>SR</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>{qsr ? qsr.toFixed(2) : '—'} kPa</mn>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* Qsr  */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowQsrDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showQsrDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>
                  Q<sub>SR</sub>
                </b>{' '}
                (Ultimate side resistance of rock socket): {QSR ? QSR.toFixed(0) : '—'} kN
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showQsrDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    We can calculate the ultimate side resistance of rock socket, Q<sub>SR</sub>, using the following formula;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>SR</mi>
                      </msub>
                      <mo>=</mo>
                      <mi>π</mi>
                      <mo>×</mo>
                      <msub>
                        <mi>B</mi>
                        <mi>r</mi>
                      </msub>
                      <mo>×</mo>
                      <msub>
                        <mi>D</mi>
                        <mi>r</mi>
                      </msub>
                      <mo>×</mo>
                      <msub>
                        <mi>q</mi>
                        <mi>SR</mi>
                      </msub>
                    </math>
                  </Typography>

                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    Since we know all the variables in this equation, we will have;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>SR</mi>
                      </msub>
                      <mo>=</mo>
                      <mi>π</mi>
                      <mo>×</mo>
                      <mo>(</mo>
                      <mn>{socketDiameter ? socketDiameter.toFixed(2) : '—'}</mn>
                      <mo>)</mo>
                      <mo>×</mo>
                      <mo>(</mo>
                      <mn>{socketLength ? socketLength.toFixed(2) : '—'}</mn>
                      <mo>)</mo>
                      <mo>×</mo>
                      <mo>(</mo>
                      <mn>{qsr ? qsr.toFixed(2) : '—'}</mn>
                      <mo>)</mo>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    And that will be equal to;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>SR</mi>
                      </msub>
                      <mo>=</mo>
                      <mi>{QSR ? QSR.toFixed(0) : '—'} kN</mi>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };

  const TipResistance = () => {
    const [showAeDetails, setShowAeDetails] = useState(false);
    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Tip Resistance
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            The tip resistance of a pile socketed into rock develops through end bearing at the base of the socket. This
            calculation determines the ultimate tip resistance (
            <i>
              Q<sub>TR</sub>
            </i>
            ) from the relationship:
          </Typography>
          <Typography
            color="text.secondary"
            sx={{
              marginTop: '15px',
              marginBottom: '10px',
              lineHeight: 1.6,
              fontWeight: 600,
            }}
          >
            <math display="block">
              <msub>
                <mi>Q</mi>
                <mi>TR</mi>
              </msub>
              <mo>=</mo>
              <msub>
                <mi>N</mi>
                <mi>ms</mi>
              </msub>
              <mo>×</mo>
              <msub>
                <mi>C</mi>
                <mi>0</mi>
              </msub>
              <mo>×</mo>
              <msub>
                <mi>A</mi>
                <mi>T</mi>
              </msub>
            </math>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            Q<sub>TR</sub>: Ultimate tip resistance of rock socket, N<sub>ms</sub>: Coefficient factor selected from Table
            4.4.8.1.2A, C<sub>0</sub>: Uniaxial compressive strength of intact rock, A<sub>T</sub>: Area of shaft tip.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* ae reduction factor */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowAeDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showAeDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>
                  Q<sub>TR</sub>
                </b>{' '}
                (Ultimate tip resistance of rock socket): {QTR ? QTR.toFixed(0) : '—'} kN
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showAeDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    You have selected N<sub>ms</sub> = {nms ? nms.toFixed(2) : '—'}. So we can calculate as follows:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>TR</mi>
                      </msub>
                      <mo>=</mo>

                      {/* (Nms) */}
                      <mo>(</mo>
                      <mn>{nms ? nms.toFixed(2) : '—'}</mn>
                      <mo>)</mo>

                      <mo>&#x00D7;</mo>

                      {/* (C0 × 10^3) */}
                      <mo>(</mo>
                      <mn>{c0 ? c0.toFixed(0) : '—'}</mn>
                      <mo>&#x00D7;</mo>
                      <msup>
                        <mn>10</mn>
                        <mn>3</mn>
                      </msup>
                      <mo>)</mo>

                      <mo>&#x00D7;</mo>

                      {/* (π × D × D / 4) */}
                      <mo>(</mo>
                      <mi>&pi;</mi>
                      <mo>&#x00D7;</mo>
                      <mn>{socketDiameter ? socketDiameter.toFixed(2) : '—'}</mn>
                      <mo>&#x00D7;</mo>
                      <mn>{socketDiameter ? socketDiameter.toFixed(2) : '—'}</mn>
                      <mo>/</mo>
                      <mn>4</mn>
                      <mo>)</mo>
                    </math>
                  </Typography>

                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    And from here we have;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>TR</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>{QTR ? QTR.toFixed(0) : '—'} kN</mn>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };

  const QUltimate = () => {
    const [showCompressionDetails, setShowCompressionDetails] = useState(false);
    const [showTensionDetails, setShowTensionDetails] = useState(false);
    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Ultimate Bearing Capacity for Compression and Uplift
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            The ultimate bearing capacity of a pile socketed into rock is evaluated separately for compression and uplift
            conditions. In compression, resistance is provided by both side shear and end bearing, while in uplift, resistance
            arises from side shear and the self-weight of the shaft. The calculation determines the ultimate axial capacity (
            <i>
              Q<sub>ult</sub>
            </i>
            ) for each case from the governing relationships:
          </Typography>
          <Typography color="text.secondary" sx={{ marginTop: '10px', marginBottom: '10px', lineHeight: 1.6, fontWeight: 600 }}>
            <math display="block">
              <msub>
                <mi>Q</mi>
                <mi>ult</mi>
              </msub>
              <mo>=</mo>
              <msub>
                <mi>Q</mi>
                <mi>SR</mi>
              </msub>
              <mo>+</mo>
              <msub>
                <mi>Q</mi>
                <mi>TR</mi>
              </msub>
              <mo>−</mo>
              <mi>W</mi>
              <mtext> (Compression)</mtext>
            </math>
            <br />
            <math display="block">
              <msub>
                <mi>Q</mi>
                <mi>ult</mi>
              </msub>
              <mo>=</mo>
              <mn>0.7</mn>
              <msub>
                <mi>Q</mi>
                <mi>SR</mi>
              </msub>
              <mo>+</mo>
              <mi>W</mi>
              <mtext> (Uplift)</mtext>
            </math>
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Qult Comporession */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowCompressionDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showCompressionDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>
                  Q<sub>ult</sub>
                </b>{' '}
                (Compression): {qUltCompression ? qUltCompression.toFixed(0) : '—'} kN
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showCompressionDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    We have already calculated side resistance (Q<sub>SR</sub>) and tip resistance (Q<sub>TR</sub>) of the socket.
                    Now let's calculate the weight of the pile, W, as follows;
                  </Typography>
                  {/* Parametric equation */}
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <mi>W</mi>
                      <mo>=</mo>
                      <msub>
                        <mi>γ</mi>
                        <mi>c</mi>
                      </msub>
                      <mo>×</mo>
                      <mi>π</mi>
                      <mo>×</mo>
                      <mfrac>
                        <mn>1</mn>
                        <mn>4</mn>
                      </mfrac>
                      <mo>×</mo>
                      <msup>
                        <mi>D</mi>
                        <mn>2</mn>
                      </msup>
                      <mo>×</mo>
                      <mi>L</mi>
                    </math>
                  </Typography>
                  {/* Live numeric substitution */}
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <mi>W</mi>
                      <mo>=</mo>
                      <mn>{gammaConcrete ? gammaConcrete.toFixed(2) : '—'}</mn>
                      <mo>×</mo>
                      <mi>π</mi>
                      <mo>×</mo>
                      <mfrac>
                        <mn>1</mn>
                        <mn>4</mn>
                      </mfrac>
                      <mo>×</mo>
                      <mo>(</mo>
                      <mn>{socketDiameter ? socketDiameter.toFixed(2) : '—'}</mn>
                      <mo>)</mo>
                      <msup>
                        <mn></mn>
                        <mn>2</mn>
                      </msup>
                      <mo>×</mo>
                      <mo>(</mo>
                      <mn>{pileLength ? pileLength.toFixed(2) : '—'}</mn>
                      <mo>)</mo>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <mi>W</mi>
                      <mo>=</mo>
                      <mn>{pileWeight ? pileWeight.toFixed(2) : '—'} kN</mn>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    Now we can calculate the ultimate bearing capacity for compression as follows;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>ult</mi>
                      </msub>
                      <mo>=</mo>
                      <msub>
                        <mi>Q</mi>
                        <mi>SR</mi>
                      </msub>
                      <mo>+</mo>
                      <msub>
                        <mi>Q</mi>
                        <mi>TR</mi>
                      </msub>
                      <mo>−</mo>
                      <mi>W</mi>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>ult</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>{QSR ? QSR.toFixed(0) : '—'}</mn>
                      <mo>+</mo>
                      <mn>{QTR ? QTR.toFixed(0) : '—'}</mn>
                      <mo>−</mo>
                      <mn>{pileWeight ? pileWeight.toFixed(0) : '—'}</mn>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>ult</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>{qUltCompression ? qUltCompression.toFixed(0) : '—'} kN</mn>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* Qult Tension */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowTensionDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showTensionDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>
                  Q<sub>ult</sub>
                </b>{' '}
                (Uplift): {qUltTension ? qUltTension.toFixed(0) : '—'} kN
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showTensionDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    We have already calculated side resistance (Q<sub>SR</sub>) and the weight of the pile, W. So we can calculate
                    Q<sub>ult</sub> for uplift as follows;
                  </Typography>
                  {/* Parametric equation */}
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>ult</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>0.7</mn>
                      <msub>
                        <mi>Q</mi>
                        <mi>SR</mi>
                      </msub>
                      <mo>+</mo>
                      <mi>W</mi>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>ult</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>0.7</mn>
                      <mn>({QSR ? QSR.toFixed(0) : '—'})</mn>
                      <mo>+</mo>
                      <mn>{pileWeight ? pileWeight.toFixed(0) : '—'}</mn>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>ult</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>{qUltTension ? qUltTension.toFixed(0) : '—'} kN</mn>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };

  const QAllowable = () => {
    const [showCompressionStaticDetails, setShowCompressionStaticDetails] = useState(false);
    const [showCompressionSeismicDetails, setShowCompressionSeismicDetails] = useState(false);
    const [showUpliftSeismicDetails, setShowUpliftSeismicDetails] = useState(false);
    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Allowable Bearing Capacity for Compression and Uplift
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            The allowable bearing capacity of a pile socketed into rock is determined by applying appropriate factors of safety to
            the ultimate capacities for both compression and uplift conditions. This ensures that the design remains within safe
            limits under service loads. In practice, separate safety factors are used for compression and uplift to account for
            differences in load transfer mechanisms, material variability, and potential construction tolerances.
          </Typography>
          <Typography color="text.secondary" sx={{ marginTop: '10px', marginBottom: '10px', lineHeight: 1.6, fontWeight: 600 }}>
            <math display="block">
              <msub>
                <mi>Q</mi>
                <mi>all</mi>
              </msub>
              <mo>=</mo>
              <mfrac>
                <msub>
                  <mi>Q</mi>
                  <mi>ult</mi>
                </msub>
                <mi>FS</mi>
              </mfrac>
            </math>
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Qall Compression Static */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowCompressionStaticDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showCompressionStaticDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>Allowable Compression Capacity - Static:</b> {qAllCompressionStatic ? qAllCompressionStatic.toFixed(0) : '—'}{' '}
                kN
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showCompressionStaticDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={2} mt={2}>
                    We have already calculated the ultimate bearing capacities. We need to divide that with the appropriate factor
                    of safety.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>all</mi>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <msub>
                          <mi>Q</mi>
                          <mi>ult</mi>
                        </msub>
                        <mi>FS</mi>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>all</mi>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <mn>{qUltCompression ? qUltCompression.toFixed(0) : '—'}</mn>
                        <mn>{FsSt ? FsSt.toFixed(2) : '—'}</mn>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>all</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>{qAllCompressionStatic ? qAllCompressionStatic.toFixed(0) : '—'} kN</mn>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* Qall Compression Seismic */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowCompressionSeismicDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showCompressionSeismicDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>Allowable Compression Capacity - Seismic:</b>{' '}
                {qAllCompressionSeismic ? qAllCompressionSeismic.toFixed(0) : '—'} kN
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showCompressionSeismicDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={2} mt={2}>
                    We have already calculated the ultimate bearing capacities. We need to divide that with the appropriate factor
                    of safety.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>all</mi>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <msub>
                          <mi>Q</mi>
                          <mi>ult</mi>
                        </msub>
                        <mi>FS</mi>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>all</mi>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <mn>{qUltCompression ? qUltCompression.toFixed(0) : '—'}</mn>
                        <mn>{FsSs ? FsSs.toFixed(2) : '—'}</mn>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>all</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>{qAllCompressionSeismic ? qAllCompressionSeismic.toFixed(0) : '—'} kN</mn>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* Qall Uplift Seismic */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowUpliftSeismicDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showUpliftSeismicDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>Allowable Uplift Capacity - Seismic:</b> {qAllTension ? qAllTension.toFixed(0) : '—'} kN
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showUpliftSeismicDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={2} mt={2}>
                    We have already calculated the ultimate bearing capacities. We need to divide that with the appropriate factor
                    of safety.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>all</mi>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <msub>
                          <mi>Q</mi>
                          <mi>ult</mi>
                        </msub>
                        <mi>FS</mi>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>all</mi>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <mn>{qUltTension ? qUltTension.toFixed(0) : '—'}</mn>
                        <mn>{FsSs ? FsSs.toFixed(2) : '—'}</mn>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <msub>
                        <mi>Q</mi>
                        <mi>all</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>{qAllTension ? qAllTension.toFixed(0) : '—'} kN</mn>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };

  const AllowableBearingCapacityVsLoads = () => {
    const [showBearingCapacityVsLoadsDetailsStatic, setShowBearingCapacityVsLoadsDetailsStatic] = useState(false);
    const [showBearingCapacityVsLoadsDetailsSeismic, setShowBearingCapacityVsLoadsDetailsSeismic] = useState(false);
    const [showBearingCapacityVsLoadsDetailsSeismicTension, setShowBearingCapacityVsLoadsDetailsSeismicTension] = useState(false);

    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Allowable Bearing Capacities Vs Loads
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
            Finally, the computed allowable bearing capacity values will be compared with the design loads you provided. This
            comparison ensures that the applied foundation pressures remain below the allowable limits, confirming that the soil
            can safely support the imposed loads without exceeding its bearing capacity in static and seismic cases.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Compression - Static Case */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowBearingCapacityVsLoadsDetailsStatic((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showBearingCapacityVsLoadsDetailsStatic ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>Allowable Compression Capacity - Static Case:</b>
                {qAllCompressionStaticIsEnough ? (
                  <>
                    <VerifiedIcon sx={{ color: 'success.main', fontSize: 22 }} />{' '}
                    <Typography variant="body2" color="success.main" fontWeight={600}>
                      OK!
                    </Typography>
                  </>
                ) : (
                  <>
                    <CancelIcon sx={{ color: 'error.main', fontSize: 22 }} />{' '}
                    <Typography variant="body2" color="error.main" fontWeight={600}>
                      Not OK!
                    </Typography>
                  </>
                )}
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showBearingCapacityVsLoadsDetailsStatic} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    We can now compare the allowable compression capacity with the load you provided for this case.
                  </Typography>

                  {qAllCompressionStaticIsEnough ? (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} textAlign={'center'}>
                        q<sub>all</sub> = {qAllCompressionStatic.toFixed(0)} kN &gt; FSt = {FSt.toFixed(0)} kN
                      </Typography>
                      <Typography variant="body2" color="success.main" fontWeight={600}>
                        The allowable bearing capacity exceeds the applied load.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} textAlign={'center'}>
                        q<sub>all</sub> = {qAllCompressionStatic.toFixed(0)} kN &lt; FSt = {FSt.toFixed(0)} kN
                      </Typography>
                      <Typography variant="body2" color="error.main" fontWeight={600}>
                        The allowable bearing capacity is less than the applied load.
                      </Typography>
                    </>
                  )}
                </Collapse>
              )}
            </Alert>
            {/* Compression - Seismic Case */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowBearingCapacityVsLoadsDetailsSeismic((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showBearingCapacityVsLoadsDetailsSeismic ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>Allowable Compression Capacity - Seismic Case:</b>
                {qAllCompressionSeismicIsEnough ? (
                  <>
                    <VerifiedIcon sx={{ color: 'success.main', fontSize: 22 }} />{' '}
                    <Typography variant="body2" color="success.main" fontWeight={600}>
                      OK!
                    </Typography>
                  </>
                ) : (
                  <>
                    <CancelIcon sx={{ color: 'error.main', fontSize: 22 }} />{' '}
                    <Typography variant="body2" color="error.main" fontWeight={600}>
                      Not OK!
                    </Typography>
                  </>
                )}
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showBearingCapacityVsLoadsDetailsSeismic} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    We can now compare the allowable compression capacity with the load you provided for this case.
                  </Typography>

                  {qAllCompressionSeismicIsEnough ? (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} textAlign={'center'}>
                        q<sub>all</sub> = {qAllCompressionSeismic.toFixed(0)} kN &gt; FSs = {FSs.toFixed(0)} kN
                      </Typography>
                      <Typography variant="body2" color="success.main" fontWeight={600}>
                        The allowable bearing capacity exceeds the applied load.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} textAlign={'center'}>
                        q<sub>all</sub> = {qAllCompressionSeismic.toFixed(0)} kN &lt; FSs = {FSs.toFixed(0)} kN
                      </Typography>
                      <Typography variant="body2" color="error.main" fontWeight={600}>
                        The allowable bearing capacity is less than the applied load.
                      </Typography>
                    </>
                  )}
                </Collapse>
              )}
            </Alert>
            {/* Uplift - Seismic Case */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowBearingCapacityVsLoadsDetailsSeismicTension((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showBearingCapacityVsLoadsDetailsSeismicTension ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>Allowable Uplift Capacity - Seismic Case:</b>
                {qAllTensionIsEnough ? (
                  <>
                    <VerifiedIcon sx={{ color: 'success.main', fontSize: 22 }} />{' '}
                    <Typography variant="body2" color="success.main" fontWeight={600}>
                      OK!
                    </Typography>
                  </>
                ) : (
                  <>
                    <CancelIcon sx={{ color: 'error.main', fontSize: 22 }} />{' '}
                    <Typography variant="body2" color="error.main" fontWeight={600}>
                      Not OK!
                    </Typography>
                  </>
                )}
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showBearingCapacityVsLoadsDetailsSeismicTension} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    We can now compare the allowable uplift capacity with the load you provided for this case.
                  </Typography>

                  {qAllTensionIsEnough ? (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} textAlign={'center'}>
                        q<sub>all</sub> = {qAllTension.toFixed(0)} kN &gt; Ft = {FTension.toFixed(0)} kN
                      </Typography>
                      <Typography variant="body2" color="success.main" fontWeight={600}>
                        The allowable bearing capacity for uplift exceeds the applied load.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} textAlign={'center'}>
                        q<sub>all</sub> = {qAllTension.toFixed(0)} kN &lt; Ft = {FTension.toFixed(0)} kN
                      </Typography>
                      <Typography variant="body2" color="error.main" fontWeight={600}>
                        The allowable bearing capacity for uplift is less than the applied load.
                      </Typography>
                    </>
                  )}
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };

  return (
    <section className="pileRockBcOutput">
      <h2>Output</h2>
      <PileRockBearingSummary />
      <SideResistance />
      <TipResistance />
      <QUltimate />
      <QAllowable />
      <AllowableBearingCapacityVsLoads />
    </section>
  );
};

export default PilerockBcResult;
